/*
 * File: EmergencyContactPage.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Fetches and styles emergency contacts for a patient and displays a list for each one
 */
import React, { useState, useEffect } from "react";
import UIListItem from "components/ui/UIListItem";
import Api from "./Api";
import PatientIdStorage from "../../store/PatientIdStore";
import UIProgress from "components/ui/UIProgress";
import UINotFound from "components/ui/UINotFound";
import UIPhoneLink from "components/ui/UIPhoneLink";
import { List, Typography, Toolbar } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import PatientChart from "services/patients/topofthechart/PatientChart";
import MainHeader from "components/MainHeader";

const styles = theme => ({
  toolbar: {
    padding: theme.spacing(2.5),
    display: "block",
    paddingBottom: theme.spacing(0.5),
    marginTop: theme.spacing(2.5)
  },
  contactNameHeader: {
    width: "100%",
    paddingLeft: theme.spacing(2.5),
    marginTop: theme.spacing(2.5),
    color: theme.palette.common.title,
    fontWeight: 400
  },
  relationshipHeader: {
    width: "100%",
    paddingLeft: theme.spacing(2.5),
    color: theme.palette.common.title
  }
});

const EmergencyContactsPage = ({ classes }) => {
  const [emergencyContacts, setEmergencyContacts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmergencyContacts();
  }, []);

  const fetchEmergencyContacts = async () => {
    const patientId = PatientIdStorage.getId();
    const emergencyContactRecords = await Api.getEmergencyContacts(patientId);
    setEmergencyContacts(emergencyContactRecords);
    setLoading(false);
  };

  const mapEmergencyContactData = () => {
    return emergencyContacts.map((contact, index) => {
      const phone = <UIPhoneLink phone={contact.phone} />;
      const nextOfKin = contact.nextOfKin ? "Yes" : "No";
      return (
        <React.Fragment key={index}>
          <div className={classes.header}>
            <Typography className={classes.contactNameHeader} variant={"h5"}>
              {contact.name}
            </Typography>
            <Typography
              className={classes.relationshipHeader}
              variant={"body1"}
            >
              {contact.relationship}
            </Typography>
          </div>
          <UIListItem label="Next of Kin" value={nextOfKin} />
          <UIListItem label="Phone" value={phone} />
        </React.Fragment>
      );
    });
  };

  if (loading) return <UIProgress />;
  if (emergencyContacts.length === 0) {
    return (
      <React.Fragment>
        <PatientChart />
        <Toolbar className={classes.toolbar}>
          <MainHeader pageTitle="Emergency Contacts" pageSubtitle="Patient" />
        </Toolbar>
        <UINotFound message="This patient has no emergency contacts listed" />
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <PatientChart />
      <Toolbar className={classes.toolbar}>
        <MainHeader pageTitle="Emergency Contacts" pageSubtitle="Patient" />
      </Toolbar>
      <List>{mapEmergencyContactData()}</List>
    </React.Fragment>
  );
};

EmergencyContactsPage.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(EmergencyContactsPage);
